import React from 'react';
import _ from 'lodash';

import {
  ApprovalTypeName,
  AuditLogWidgetDocument,
  Partner_ReturnAuthorizationActionsWidgetDocument,
  Partner_ReturnAuthorizationLifeCycleWidgetDocument,
  QueryReturnAuthorizationsFilterInput,
  useApproveReturnAuthorizationMutation,
  useTransactionListActivity_ReturnAuthorizationListTabLazyQuery
} from 'generated/graphql';

import { DateTime } from 'luxon';
import { listQueryUtil } from 'iron-forge-react-modules';
import * as types from './types';



const useListQuery = listQueryUtil.genUseListQuery<types.IColumnId>();



export interface ILineItem {
  id: string;
  sku: string;
  productName: string;
  quantity: number;
  reasonForReturn: string;
}

export interface IReturnAuthorization {
  id: string;
  orderId: string;
  returnNumber: string;
  returnDate: DateTime;
  approvalStatus: ApprovalTypeName;
  itemsReceived: number;
  acceptanceStatus: ApprovalTypeName;
  refundStatus: ApprovalTypeName;

  lineItems: ILineItem[];
}

export interface IData {
  returnAuthorizations: IReturnAuthorization[];
}

export function useQuery(storeId: string) {
  return useListQuery({
    useLazyQuery: useTransactionListActivity_ReturnAuthorizationListTabLazyQuery,
    genFilter2GqlFilter: () => _.partial(__filter2GqlFilter,
      storeId),
    clientColumn2GqlSortField: {
      returnNumber: 'returnNumber',
      returnDate: 'returnDate'
    },
    initQuerySort: {
      columnId: 'returnDate',
      direction: 'descending'
    },
    defaultFilterArgs: {},
    gqlResultData2GqlPageInfo: gqlResultData =>
      gqlResultData?.returnAuthorizations.pageInfo,

    gqlResultData2TupleData: (gqlTuple): IData => ({
      returnAuthorizations: _.map(gqlTuple.data?.returnAuthorizations.nodes ?? [], (node): IReturnAuthorization => ({
        id: node.id,
        orderId: node.order.id,
        returnNumber: node.returnNumber,
        returnDate: DateTime.fromISO(node.returnDate),
        approvalStatus: node.approval?.approvalType.name ?? 'pending',
        acceptanceStatus: node.acceptance?.approvalType.name ?? 'pending',
        itemsReceived: node.itemReceipt?.lineItems.length ?? -1, // Set to -1 for the UI
        refundStatus: node.refund?.approvalType.name ?? 'pending',
        lineItems: _.map(node.lineItems, (lineItem): ILineItem => ({
          id: lineItem.id,
          sku: lineItem.product.sku,
          productName: lineItem.product.name,
          quantity: lineItem.quantity,
          reasonForReturn: lineItem.reasonForReturn
        }))
      }))
    })
  });
}



function __filter2GqlFilter(storeId: string, filter?: types.IFilter): QueryReturnAuthorizationsFilterInput {
  return {
    storeId,
    after: filter?.after ? DateTime.fromJSDate(filter.after).toISO() : undefined,
    before: filter?.before ? DateTime.fromJSDate(filter.before).toISO() : undefined
  };
}




export function useApproveReturnAuthorization() {
  const [fnMutate, tuple] = useApproveReturnAuthorizationMutation({});

  const fnWrappedMutate = React.useCallback(
    async (returnAuthorizationId: string, approvalTypeName: ApprovalTypeName) => {
      await fnMutate({
        variables: {
          approvalTypeName,
          returnAuthorizationId
        },
        refetchQueries: __genRefetchQueries(returnAuthorizationId)
      });
    },
    [
      fnMutate
    ]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}



function __genRefetchQueries(returnAuthorizationId: string) {
  return [
    {
      query: Partner_ReturnAuthorizationLifeCycleWidgetDocument,
      variables: {
        returnAuthorizationId,
        queryAsCustomer: false
      }
    },
    {
      query: Partner_ReturnAuthorizationActionsWidgetDocument,
      variables: { returnAuthorizationId }
    },
    {
      query: AuditLogWidgetDocument,
      variables: {
        id: returnAuthorizationId,
        isQueryingOrder: false
      }
    }
  ];
}
